import { useEffect, useState } from 'react';
import { FieldPath, UseFormReturn, useWatch } from 'react-hook-form';

import { ValueType } from 'components/DebounceSelect';
import DateTimeInput from 'components/input/DateTimeInput';
import { LoadRefPillInput } from 'components/input/LoadRefPillInput';
import { RHFLocationSelect } from 'components/input/RHFLocationSelect';
import { InputValue } from 'components/input/RHFTextInput';
import { Grid } from 'components/layout';
import { Typography } from 'components/typography/Typography';
import { useExtendedFormContext } from 'contexts/extendedFormContext';
import { useFieldAttributes } from 'hooks/useLoadContext';
import { LoadStopsItemTypes, NormalizedLoad, TMSLocation } from 'types/Load';
import { getFieldAttribute } from 'types/LoadAttributes';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';

import { CreateLocationButton } from '../components/CreateLocationButton';
import LocationCreatorDialog from '../components/LocationCreatorDialog';
import { LoadBuildingTextInput } from './UnifiedAljexLoadForm';
import { AljexStopAddressFields, isAljexCustomStopLocation } from './utils';

export type StopFormProps = {
  stopType: LoadStopsItemTypes;
  stopKey: string;
  formMethods: UseFormReturn<NormalizedLoad>;
  isLoadingLocations: boolean;
  locations: Maybe<TMSLocation[]>;
  handleRefreshLocations: () => void;
  setLocations: React.Dispatch<React.SetStateAction<Maybe<TMSLocation[]>>>;
  originalSuggestionData?: NormalizedLoad;
  tmsID: number;
  isCreateMode: boolean;
};

export default function StopForm({
  stopType,
  stopKey,
  formMethods,
  isLoadingLocations,
  locations,
  handleRefreshLocations,
  setLocations,
  originalSuggestionData,
  tmsID,
  isCreateMode,
}: StopFormProps): React.JSX.Element {
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = formMethods;
  const fieldAttrs = useFieldAttributes();
  const pickupRefNumberAttr = getFieldAttribute(fieldAttrs, 'pickup.refNumber');
  const consigneeRefNumberAttr = getFieldAttribute(
    fieldAttrs,
    'consignee.refNumber'
  );

  const [isLocationCreatorOpen, setIsLocationCreatorOpen] = useState(false);

  const isStopType = stopType === LoadStopsItemTypes.Stop;

  const addrPath = (
    k:
      | 'name'
      | 'addressLine1'
      | 'addressLine2'
      | 'city'
      | 'state'
      | 'zip'
      | 'country'
  ) => `${stopKey}.address.${k}` as FieldPath<NormalizedLoad>;

  const watchedAddress = useWatch({
    control,
    name: `${stopKey}.address` as FieldPath<NormalizedLoad>,
  }) as unknown as AljexStopAddressFields;
  const watchedLocationID = useWatch({
    control,
    name: `${stopKey}.externalTMSID` as FieldPath<NormalizedLoad>,
  }) as MaybeUndef<string>;

  const showCustomLocationCallout =
    !isStopType && isAljexCustomStopLocation(watchedLocationID, watchedAddress);
  const { aiDefaultValues } = useExtendedFormContext();

  useEffect(() => {
    if (watchedLocationID) {
      const selectedLoc = locations?.find(
        (loc) => loc.externalTMSID === watchedLocationID
      );

      if (selectedLoc) {
        Object.entries(selectedLoc).forEach(([key, value]) => {
          if (key === 'contact' || key === 'phone') {
            return;
          }
          setValue(`${stopKey}.${key}` as FieldPath<NormalizedLoad>, value, {
            shouldDirty: aiDefaultValues,
          });

          const addrKeyMap: Record<string, string> = {
            name: 'name',
            addressLine1: 'addressLine1',
            addressLine2: 'addressLine2',
            city: 'city',
            state: 'state',
            zipCode: 'zip', // nested uses zip
            country: 'country',
          };

          const nestedKey = addrKeyMap[key];
          if (nestedKey) {
            setValue(
              `${stopKey}.address.${nestedKey}` as FieldPath<NormalizedLoad>,
              value as any,
              {
                shouldDirty: aiDefaultValues,
              }
            );
          }
        });
      }
    }
  }, [watchedLocationID, locations, setValue, stopKey, aiDefaultValues]);

  useEffect(() => {
    if (!watchedLocationID) {
      return;
    }

    const selectedLoc = locations?.find(
      (loc) => loc.externalTMSID === watchedLocationID
    );

    if (!selectedLoc) {
      return;
    }

    const timeoutId = setTimeout(() => {
      const lower = (v?: string) => (v ?? '').toString().toLowerCase();

      const {
        name: watchedName,
        city: watchedCity,
        state: watchedState,
        addressLine1: watchedAddressLine1,
        addressLine2: watchedAddressLine2,
        zip: watchedZip,
      } = (watchedAddress ?? {}) as any;

      const hasMismatch =
        (selectedLoc?.name &&
          lower(watchedName) !== lower(selectedLoc?.name)) ||
        (selectedLoc?.addressLine1 &&
          lower(watchedAddressLine1) !== lower(selectedLoc?.addressLine1)) ||
        (selectedLoc?.addressLine2 &&
          lower(watchedAddressLine2) !== lower(selectedLoc?.addressLine2)) ||
        (selectedLoc?.city &&
          lower(watchedCity) !== lower(selectedLoc?.city)) ||
        (selectedLoc?.state &&
          lower(watchedState) !== lower(selectedLoc?.state)) ||
        (selectedLoc?.zipCode &&
          lower(watchedZip) !== lower(selectedLoc?.zipCode)) ||
        (selectedLoc?.country &&
          lower((watchedAddress as any)?.country as any) !==
            lower(selectedLoc?.country));

      if (hasMismatch) {
        setValue(`${stopKey}.externalTMSID` as FieldPath<NormalizedLoad>, '', {
          shouldDirty: true,
        });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [
    watchedLocationID,
    watchedAddress,
    locations,
    setValue,
    stopKey,
    formMethods,
  ]);

  const handleLocationCreated = (newLocation: TMSLocation) => {
    const updatedLocations = [...(locations || []), newLocation];
    setLocations(updatedLocations);

    setValue(
      `${stopKey}.externalTMSID` as FieldPath<NormalizedLoad>,
      newLocation.externalTMSID,
      {
        shouldDirty: true,
      }
    );
  };

  return (
    <Grid cols='4' gap='lg' className='mx-0 w-full'>
      <div className='col-span-4'>
        <RHFLocationSelect
          required={false}
          disabled={isStopType}
          name={`${stopKey}.externalTMSID` as FieldPath<NormalizedLoad>}
          label='Location'
          control={control}
          errors={errors}
          locations={locations}
          setLocations={setLocations}
          isLoadingLocations={isLoadingLocations}
          handleRefreshLocations={handleRefreshLocations}
          placeholder={isCreateMode ? 'Search by name or address' : 'See below'}
          labelIsName={true}
          valueRenderer={(selectedOption: ValueType) => {
            if (selectedOption.name) {
              return {
                label: selectedOption.name,
                value: selectedOption.value,
              };
            }

            const location = locations?.find(
              (loc) => loc.externalTMSID === selectedOption.value
            );
            const displayName =
              location?.name || selectedOption.label || selectedOption.value;
            return {
              label: displayName,
              value: selectedOption.value,
            };
          }}
        />
      </div>

      {showCustomLocationCallout && (
        <div className='col-span-4 p-2 bg-info-50 border border-info-200 rounded-md'>
          <Typography className='text-xs font-medium text-info-800'>
            Custom location
          </Typography>
          <Typography className='text-xs text-info-600'>
            You don't need to choose a saved location. Confirm the address
            fields below and continue.
          </Typography>
        </div>
      )}

      <CreateLocationButton
        onClick={() => setIsLocationCreatorOpen(true)}
        disabled={isLoadingLocations}
      />

      <div className='col-span-4'>
        <LoadBuildingTextInput
          name={addrPath('name')}
          label='Company Name'
          required={true}
          placeholder='Company Name'
          readOnly={isStopType}
        />
      </div>

      <div className='col-span-4'>
        <LoadBuildingTextInput
          name={addrPath('addressLine1')}
          label='Street Address'
          required={true}
          placeholder='123 Main Street'
          readOnly={isStopType}
        />
      </div>

      <div className='col-span-4'>
        <LoadBuildingTextInput
          name={addrPath('addressLine2')}
          label='Address Line 2'
          required={false}
          placeholder='Suite 100, Dock 5, etc. (optional)'
          readOnly={isStopType}
        />
      </div>

      <div className='col-span-3'>
        <LoadBuildingTextInput
          name={addrPath('city')}
          label='City'
          required={!isStopType}
          readOnly={isStopType}
        />
      </div>

      <div className='col-span-1'>
        <LoadBuildingTextInput
          name={addrPath('state')}
          label='State'
          required={!isStopType}
          readOnly={isStopType}
        />
      </div>

      <div className='col-span-3'>
        <LoadBuildingTextInput
          name={addrPath('zip')}
          label='ZIP'
          placeholder='12345'
          readOnly={isStopType}
        />
      </div>

      <div className='col-span-1'>
        <LoadBuildingTextInput
          name={addrPath('country')}
          label='Country'
          readOnly={true}
        />
      </div>

      <>
        <div className='col-span-4 text-sm font-medium text-neutral-700 mb-2 mt-3'>
          Contact Information
        </div>

        <div className='col-span-2'>
          <LoadBuildingTextInput
            name={`${stopKey}.contact` as FieldPath<NormalizedLoad>}
            label='Contact Person'
            required={false}
            placeholder='Contact name'
            readOnly={isStopType}
          />
        </div>

        <div className='col-span-2'>
          <LoadBuildingTextInput
            name={`${stopKey}.phone` as FieldPath<NormalizedLoad>}
            label='Phone'
            required={false}
            placeholder='(555) 123-4567'
            inputValue={InputValue.PHONE_NUMBER}
            readOnly={isStopType}
          />
        </div>

        <div className='col-span-4'>
          <LoadBuildingTextInput
            name={`${stopKey}.email` as FieldPath<NormalizedLoad>}
            label='Email'
            required={false}
            placeholder='contact@company.com'
            readOnly={isStopType}
          />
        </div>
      </>

      {stopType === LoadStopsItemTypes.Pickup && (
        <>
          <div className='col-span-4 text-sm font-medium text-neutral-700 mb-2 mt-3'>
            Reference & Appointment Information
          </div>

          <div className='col-span-4'>
            <LoadBuildingTextInput
              name='pickup.businessHours'
              label='Business Hours'
              required={false}
              placeholder='0800-1700'
            />
          </div>
          {!pickupRefNumberAttr?.isNotSupported && (
            <div className='col-span-4'>
              <LoadRefPillInput
                name='pickup.refNumber'
                label='Reference Number'
                control={control}
                originalSuggestionData={originalSuggestionData || getValues()}
                disabled={Boolean(pickupRefNumberAttr?.isReadOnly)}
              />
            </div>
          )}

          <div className='col-span-4'>
            <DateTimeInput
              control={control}
              name='pickup.readyTime'
              label='Ready Time'
              required={true}
              preventNormalizedLabelTZ={true}
            />
          </div>
          <div className='col-span-4'>
            <DateTimeInput
              control={control}
              name='pickup.apptStartTime'
              label='Appointment Time'
              required={false}
              preventNormalizedLabelTZ={true}
            />
          </div>
          <div className='col-span-4'>
            <LoadBuildingTextInput
              name='pickup.apptNote'
              label='Appointment Notes'
              required={false}
              placeholder='Call ahead, loading dock, etc.'
            />
          </div>
        </>
      )}

      {stopType === LoadStopsItemTypes.Dropoff && (
        <>
          <div className='col-span-4 text-sm font-medium text-neutral-700 mb-2 mt-3'>
            Reference & Appointment Information
          </div>

          <div className='col-span-4'>
            <LoadBuildingTextInput
              name='consignee.businessHours'
              label='Business Hours'
              required={false}
              placeholder='0800-1700'
            />
          </div>
          {!consigneeRefNumberAttr?.isNotSupported && (
            <div className='col-span-4'>
              <LoadRefPillInput
                name='consignee.refNumber'
                label='Reference Number'
                control={control}
                originalSuggestionData={originalSuggestionData || getValues()}
                disabled={Boolean(consigneeRefNumberAttr?.isReadOnly)}
              />
            </div>
          )}
          <div className='col-span-4'>
            <DateTimeInput
              control={control}
              name='consignee.mustDeliver'
              label='Must Deliver By'
              required={false}
              hideTimePicker={true}
              preventNormalizedLabelTZ={true}
            />
          </div>

          <div className='col-span-4'>
            <DateTimeInput
              control={control}
              name='consignee.apptStartTime'
              label='Appointment Time'
              required={false}
              preventNormalizedLabelTZ={true}
            />
          </div>
          <div className='col-span-4'>
            <LoadBuildingTextInput
              name='consignee.apptNote'
              label='Appointment Notes'
              required={false}
              placeholder='Call ahead, loading dock, etc.'
            />
          </div>
        </>
      )}

      {/* Location Creator Dialog */}
      <LocationCreatorDialog
        isOpen={isLocationCreatorOpen}
        onClose={() => setIsLocationCreatorOpen(false)}
        onLocationCreated={handleLocationCreated}
        tmsID={tmsID}
        initialData={undefined}
      />
    </Grid>
  );
}
